import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, FileText, Factory, Users, Database, ChevronRight, ChevronUp, KeyRound, LogOut, Shield, Mail, BellRing, PackageOpen, Package, Truck, IndianRupee, ShieldCheck, IdCard, Siren, Ban, Car, ClipboardList, Building2, UserSquare2, BarChart3, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BBS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/hm9k398i_Screenshot%202019-07-29%20at%201.08.27%20PM.png";
const OPSIQ_LOCKUP = "https://customer-assets-lxgj4vgw.emergentagent.net/job_production-ops-23/artifacts/m4d6gaj0_ops-iq-lockup-1200.png";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/enquiries", label: "Enquiry Costing", icon: Inbox, testid: "nav-enquiries" },
  { to: "/bulletins", label: "Operation Bulletin", icon: FileText, testid: "nav-bulletins" },
  { to: "/line-tracking", label: "Line Tracking", icon: Factory, testid: "nav-line-tracking" },
  { to: "/samples", label: "Sample Register", icon: PackageOpen, testid: "nav-samples" },
  { to: "/price-list", label: "Price List", icon: IndianRupee, testid: "nav-price-list" },
  { to: "/fabric-trim", label: "Fabric / Trim Request", icon: Package, testid: "nav-ftr" },
  { to: "/masters", label: "Masters", icon: Database, testid: "nav-masters" },
];

const ADMIN_NAV = [
  { to: "/users", label: "User Management", icon: Users, testid: "nav-users" },
  { to: "/admin/roles-permissions", label: "Roles & Permissions", icon: Shield, testid: "nav-roles" },
  { to: "/admin/email-configuration", label: "Email Configuration", icon: Mail, testid: "nav-email-config" },
  { to: "/admin/notifications", label: "Notifications", icon: BellRing, testid: "nav-notifications-settings" },
  { to: "/admin/courier-templates", label: "Courier Tracking URLs", icon: Truck, testid: "nav-courier-templates" },
];

// CheckPoint (Visitor Entry) — merged from gate-check-6. Each item is gated by
// a capability so guards see only what they need at the gate cabin.
const CHECKPOINT_NAV = [
  { to: "/checkpoint",            label: "Dashboard",       icon: LayoutDashboard, testid: "nav-cp-dashboard", cap: "view_checkpoint" },
  { to: "/checkpoint/checkin",    label: "Gate Check-in",   icon: IdCard,          testid: "nav-cp-checkin",   cap: "checkpoint_checkin" },
  { to: "/checkpoint/pre-register",label: "Pre-Register",   icon: Ticket,          testid: "nav-cp-preregister", cap: "pre_register_visitor" },
  { to: "/checkpoint/visitors",   label: "Visitor Log",     icon: ClipboardList,   testid: "nav-cp-visitors",  cap: "view_checkpoint" },
  { to: "/checkpoint/emergency",  label: "Emergency View",  icon: Siren,           testid: "nav-cp-emergency", cap: "view_checkpoint" },
  { to: "/checkpoint/vehicles",   label: "Vehicles",        icon: Car,             testid: "nav-cp-vehicles",  cap: "view_checkpoint" },
  { to: "/checkpoint/deliveries", label: "Deliveries",      icon: Package,         testid: "nav-cp-deliveries",cap: "view_checkpoint" },
  { to: "/checkpoint/blacklist",  label: "Blacklist",       icon: Ban,             testid: "nav-cp-blacklist", cap: "manage_blacklist" },
  { to: "/checkpoint/analytics",  label: "Analytics",       icon: BarChart3,       testid: "nav-cp-analytics", cap: "view_visitor_analytics" },
  { to: "/checkpoint/reports",    label: "Reports",         icon: FileText,        testid: "nav-cp-reports",   cap: "export_visitor_reports" },
  { to: "/checkpoint/admin/gates",label: "Gates",           icon: Building2,       testid: "nav-cp-gates",     cap: "manage_gates_hosts" },
  { to: "/checkpoint/admin/hosts",label: "Hosts",           icon: UserSquare2,     testid: "nav-cp-hosts",     cap: "manage_gates_hosts" },
];

export default function Sidebar({ user: userProp, onNavigate }) {
  const { user: userCtx, logout } = useAuth();
  const user = userProp || userCtx;
  const nav = useNavigate();
  const caps = new Set(user?.capabilities || []);
  const isAdminRole = user?.role === "admin" || user?.role === "admin_addl";
  const has = (k) => isAdminRole || caps.has(k);
  const CAP_BY_PATH = { "/enquiries": "view_enquiries", "/bulletins": "view_bulletins", "/line-tracking": "view_line_tracking", "/samples": "view_samples", "/price-list": "view_price_list", "/fabric-trim": "view_ftr_requests", "/masters": "view_masters" };
  const items = NAV.filter((n) => (!n.roles || n.roles.includes(user?.role)) && (!CAP_BY_PATH[n.to] || has(CAP_BY_PATH[n.to])));
  const checkpointItems = CHECKPOINT_NAV.filter((n) => has(n.cap));
  const showCheckpoint = checkpointItems.length > 0;
  const showAdmin = isAdminRole;

  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const doLogout = async () => {
    setMenuOpen(false);
    await logout();
    onNavigate?.();
    nav("/login");
  };

  const openChangePwd = () => {
    setMenuOpen(false);
    setPwd({ current: "", next: "", confirm: "" });
    setPwdOpen(true);
  };

  const submitChangePwd = async () => {
    if (!pwd.current || !pwd.next) { toast.error("Fill both fields"); return; }
    if (pwd.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("New password and confirmation don't match"); return; }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password changed");
      setPwdOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Change failed");
    }
    setPwdBusy(false);
  };

  return (
    <aside className="bg-sidebar h-full w-64 shrink-0 flex flex-col border-r border-sidebar" data-testid="sidebar">
      {/* Brand — logo on top, OPS IQ label below */}
      <div className="px-5 pt-6 pb-5 border-b border-sidebar">
        <div className="flex flex-col items-start gap-3">
          <div className="inline-flex items-center w-full" data-testid="sidebar-bbs-logo">
            <img
              src={BBS_LOGO}
              alt="BBS Global Pvt. Ltd."
              className="w-full max-w-[190px] h-auto object-contain select-none pointer-events-none"
              draggable="false"
            />
          </div>
          <div className="inline-flex items-center w-full pt-1" data-testid="sidebar-opsiq-logo">
            <img
              src={OPSIQ_LOCKUP}
              alt="Ops IQ · Shop Floor · Planning · Output"
              className="w-full max-w-[190px] h-auto object-contain select-none pointer-events-none"
              draggable="false"
            />
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <div className="px-2 pb-2 text-[10px] tracking-widest uppercase text-sidebar-muted font-heading font-semibold">Operations</div>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.to === "/"}
            data-testid={it.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-md text-[13.5px] font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-sidebar-active text-white shadow-sm"
                  : "text-sidebar-fg hover:bg-slate-100 dark:hover:bg-white/5"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <it.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="flex-1">{it.label}</span>
                {isActive && <ChevronRight className="h-3.5 w-3.5 opacity-80" />}
              </>
            )}
          </NavLink>
        ))}

        {showCheckpoint && (
          <>
            <div className="px-2 pt-5 pb-2 text-[10px] tracking-widest uppercase text-sidebar-muted font-heading font-semibold">CheckPoint</div>
            {checkpointItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/checkpoint"}
                data-testid={it.testid}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-md text-[13.5px] font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-sidebar-active text-white shadow-sm"
                      : "text-sidebar-fg hover:bg-slate-100 dark:hover:bg-white/5"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <it.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span className="flex-1">{it.label}</span>
                    {isActive && <ChevronRight className="h-3.5 w-3.5 opacity-80" />}
                  </>
                )}
              </NavLink>
            ))}
          </>
        )}

        {showAdmin && (
          <>
            <div className="px-2 pt-5 pb-2 text-[10px] tracking-widest uppercase text-sidebar-muted font-heading font-semibold">Administration</div>
            {ADMIN_NAV.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-md text-[13.5px] font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-sidebar-active text-white shadow-sm"
                      : "text-sidebar-fg hover:bg-slate-100 dark:hover:bg-white/5"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <it.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span className="flex-1">{it.label}</span>
                    {isActive && <ChevronRight className="h-3.5 w-3.5 opacity-80" />}
                  </>
                )}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User footer — click name → menu (Change password, Sign out) */}
      <div className="p-3 border-t border-sidebar">
        <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="sidebar-user-menu-btn"
              className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="text-sidebar-fg text-sm font-medium truncate">{user?.name}</div>
                <div className="text-sidebar-muted text-[11px] truncate capitalize">{user?.role}</div>
              </div>
              <ChevronUp className="h-4 w-4 text-sidebar-muted" />
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-56 p-1" data-testid="sidebar-user-menu">
            <div className="px-2 py-2 border-b border-slate-100 dark:border-white/10">
              <div className="text-[11px] text-slate-500 dark:text-slate-400">Signed in as</div>
              <div className="text-[13px] font-medium truncate">{user?.email}</div>
            </div>
            <button
              onClick={openChangePwd}
              data-testid="change-password-menu-item"
              className="w-full flex items-center gap-2 px-2 py-2 rounded text-[13px] hover:bg-slate-100 dark:hover:bg-white/5 mt-1"
            >
              <KeyRound className="h-4 w-4" /> Change password
            </button>
            <button
              onClick={doLogout}
              data-testid="sidebar-logout-btn"
              className="w-full flex items-center gap-2 px-2 py-2 rounded text-[13px] text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-brand" /> Change password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Current password</label>
              <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="pwd-current" className="mt-1" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">New password</label>
              <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="pwd-new" className="mt-1" />
              <div className="text-[10.5px] text-slate-400 mt-1">Minimum 6 characters.</div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">Confirm new password</label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)}>Cancel</Button>
            <Button onClick={submitChangePwd} disabled={pwdBusy} className="bg-brand hover:bg-brand-dark text-white" data-testid="pwd-submit-btn">
              {pwdBusy ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  );
}
